import { useState } from "react";
import { FaUserGear } from "react-icons/fa6";
import "./user-profile-sidemodal.style.scss";

// components
import DialogModal from "../dialog-modal/dialog-modal.component";
import { Button } from "../ui/button";
import SelectBox from "../form-inputs/select-box.component";
import FormError from "../form-error/form-error.component";

// global strings and functions
import strings from "../../global/strings";
import { queryClient } from "../../global/query-client";
import { useSession } from "../../queries/auth.queries";
import {
	EmployeePermissions,
	buildProfileSnapshotPayload,
	employeesQueryKey,
	useEmployeeDetails,
	useUpdateEmployee,
} from "../../queries/employee.queries";

interface EmployeeRoleManagerProps {
	userInfoId: string;
	empPermission?: EmployeePermissions;
	currentPageNum?: number;
}

function getRoleLabel(role?: "owner" | "user" | null) {
	return strings.role.find((option: { id: string; value: string }) => option.id === role)
		?.value ?? "—";
}

// Split out alongside EmployeeLoginManager/EmployeeStatusToggle: a role
// change is an access-control decision (backend forces a logout on change,
// and only an owner is authorized to make it — see updateUser's rank check
// in billing_system_BE/controllers/user.controller.js), not a personal-info
// edit, so it gets the same treatment — its own affordance, own confirm
// dialog, own mutation, independent of the profile-edit form. Self-contained:
// given just a userInfoId it fetches its own useEmployeeDetails (deduped
// against the profile's own call) for the current role, and useSession for
// whether the viewer is even allowed to change it.
function EmployeeRoleManager({
	userInfoId,
	empPermission,
	currentPageNum,
}: EmployeeRoleManagerProps) {
	const { data: session } = useSession();
	const { data: employeeDetails } = useEmployeeDetails(userInfoId);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedRole, setSelectedRole] = useState<"owner" | "user">("user");

	const roleMutation = useUpdateEmployee();

	const currentRole = employeeDetails?.user_role;
	const isSelf = userInfoId === session?.user_info_id;
	// Mirrors updateUser's own authorization: only an owner may change a
	// role, and never their own (self-role-change is blocked server-side —
	// hiding the affordance here avoids a confusing "Unauthorized" round trip).
	const canChangeRole =
		empPermission?.edit && session?.user_role === "owner" && !isSelf;

	const handleOpen = () => {
		setSelectedRole(currentRole ?? "user");
		roleMutation.reset();
		setDialogOpen(true);
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) roleMutation.reset();
		setDialogOpen(open);
	};

	const handleConfirm = async () => {
		if (selectedRole === currentRole || !employeeDetails?.user) {
			setDialogOpen(false);
			return;
		}

		try {
			await roleMutation.mutateAsync({
				employeeData: {
					...buildProfileSnapshotPayload(employeeDetails.user),
					user_role: selectedRole,
				},
				page: currentPageNum,
			});
		} catch {
			return; // FormError below renders the message
		}

		queryClient.invalidateQueries({ queryKey: employeesQueryKey });
		queryClient.invalidateQueries({ queryKey: ["employeeDetails", userInfoId] });
		roleMutation.reset();
		setDialogOpen(false);
	};

	return (
		<div className="user-info-container">
			<div className="section-header-row">
				<label className="input-label">Employee Role</label>
				{canChangeRole && (
					<Button
						type="button"
						size="sm"
						variant="outline"
						onClick={handleOpen}
					>
						<FaUserGear /> Change Role
					</Button>
				)}
			</div>
			<div className="display-value">{getRoleLabel(currentRole)}</div>

			<DialogModal
				open={dialogOpen}
				onOpenChange={handleOpenChange}
				title="Change Employee Role"
				description="This takes effect immediately and signs them out of this company — they'll need to log in again."
				footer={
					<>
						<Button
							variant="outline"
							onClick={() => handleOpenChange(false)}
							disabled={roleMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							onClick={handleConfirm}
							disabled={roleMutation.isPending || selectedRole === currentRole}
						>
							{roleMutation.isPending ? "Saving..." : "Confirm"}
						</Button>
					</>
				}
			>
				<FormError
					isError={roleMutation.isError}
					error={roleMutation.error}
					fallback="Something went wrong. Please try again."
				/>
				<div className="input-wrapper">
					<label className="input-label">Role</label>
					<SelectBox
						name="user_role"
						value={selectedRole}
						onChange={(e) =>
							setSelectedRole(e.target.value as "owner" | "user")
						}
						options={strings.role}
					/>
				</div>
			</DialogModal>
		</div>
	);
}

export default EmployeeRoleManager;
